import React, { useState, useEffect } from "react";
import fetchApi from "../Lib/api";
import { IconArrowRight, IconX, IconMail, IconUser, IconPhone, IconBrandLinkedin, IconUpload, IconCheck, IconExternalLink } from "@tabler/icons-react";

const JoinWaitlistForm = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    linkedin: "",
    phone: "",
    cv: null, // For file upload
    mixpanel_distinct_id: null,
  });

  const [notification, setNotification] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "file" ? files[0] : value,
    }));
  };
  
  const handleCheckboxChange = () => {
    const newCheckedState = !isChecked;
    setIsChecked(newCheckedState);
    if (window.mixpanel) {
      window.mixpanel.track('Waitlist Terms Toggled', { checked: newCheckedState });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isChecked) {
      // Prevent form submission if checkbox is not checked
      alert("You must agree to the Terms & Conditions and Privacy Policy!");
      if (window.mixpanel) {
        window.mixpanel.track('Join Waitlist Form Submission Failure', { error: 'Terms not accepted' });
      }
    } else {
      if (window.mixpanel) {
        formData.mixpanel_distinct_id = window.mixpanel.get_distinct_id();
      }
      const formDataToSend = new FormData();
      for (const [key, value] of Object.entries(formData)) {
        formDataToSend.append(`join_waitlist[${key}]`, value);
      }

      try {
        const response = await fetchApi(
          "/join_waitlist",
          "POST",
          formDataToSend,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const data = await response.json();

        if (response.ok) {
          if (window.mixpanel) {
            window.mixpanel.track('Join Waitlist Form Submission Success');
          }
          setNotification(
            <div role="alert" className="text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <IconCheck size={32} className="text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-green-700 mb-2">Welcome to the Waitlist!</h3>
                <p className="text-gray-700 mb-4">{data.message}</p>
                <p className="text-gray-600">
                  While you wait, feel free to connect with us on social media or
                  check out some useful links.
                </p>
              </div>
            </div>
          );
          setFormData({
            first_name: "",
            last_name: "",
            email: "",
            linkedin: "",
            phone: "",
            cv: null,
          });
          setIsSubmitted(true);
        } else {
          if (window.mixpanel) {
            window.mixpanel.track('Join Waitlist Form Submission Failure', { error: data.message || "There was an error submitting the form. Please try again." });
          }
          setNotification(
            <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <IconX size={18} className="text-red-600" />
                </div>
                <p className="text-red-700 font-medium">
                  {data.message ||
                    "There was an error submitting the form. Please try again."}
                </p>
              </div>
            </div>
          );
        }
      } catch (error) {
        if (window.mixpanel) {
          window.mixpanel.track('Join Waitlist Form Submission Failure', { error: 'An error occurred. Please try again later.' });
        }
        setNotification(
          <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <IconX size={18} className="text-red-600" />
              </div>
              <p className="text-red-700 font-medium">
                An error occurred. Please try again later.
              </p>
            </div>
          </div>
        );
      }
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
    if (window.mixpanel) {
      window.mixpanel.track('Join Waitlist Modal Opened');
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsSubmitted(false);
    setNotification("");
    if (window.mixpanel) {
      window.mixpanel.track('Join Waitlist Modal Closed');
    }
  };

  return (
    <>
      <button
        onClick={openModal}
        className="group relative bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold px-8 py-3 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105 hover:shadow-xl flex items-center gap-2"
      >
        <IconUser size={18} />
        Try it for Free
        <IconArrowRight 
          size={16} 
          className="transition-transform duration-300 group-hover:translate-x-1" 
        />
      </button>

      {isModalOpen && (
        <div
          id="formModal"
          className="modal text-left shadow-sm fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-start justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target.id === "formModal") closeModal();
          }}
        >
          <div style={{maxHeight: "95vh"}} className="modal-content bg-white max-w-[380px] lg:max-w-[750px] rounded-2xl relative text-black shadow-2xl border border-gray-100 overflow-y-auto">
            <div className="modal-body">
              <form
                onSubmit={handleSubmit}
                className="space-y-6 p-6 lg:p-20"
              >
                {isSubmitted ? (
                  <div id="notification_area" className="py-8">{notification}</div>
                ) : (
                  <>
                    {notification && !isSubmitted && (
                      <div id="notification_area" className="mb-6">{notification}</div>
                    )}
                    
                    {/* Header */}
                    <div className="text-center mb-10 relative">
                      <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <IconUser size={28} className="text-white" />
                      </div>
                      <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        Join the Waitlist
                      </h1>
                      <p className="text-gray-600 max-w-md mx-auto">
                        Be the first to know when we launch. Get exclusive early access and special offers.
                      </p>
                      <button
                        type="button"
                        className="absolute top-0 right-0 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                        onClick={closeModal}
                      >
                        <IconX size={20} className="text-gray-600" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* First Name Input */}
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-gray-700">
                          First Name
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <IconUser size={18} className="text-gray-400" />
                          </div>
                          <input
                            type="text"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleChange}
                            required
                            placeholder="Enter your first name"
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                          />
                        </div>
                      </div>

                      {/* Last Name Input */}
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-gray-700">
                          Last Name
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <IconUser size={18} className="text-gray-400" />
                          </div>
                          <input
                            type="text"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                            required
                            placeholder="Enter your last name"
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Email Address Input */}
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Email Address
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <IconMail size={18} className="text-gray-400" />
                        </div>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="Enter your email address"
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                        />
                      </div>
                    </div>

                    {/* LinkedIn Profile Input */}
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        LinkedIn Profile
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <IconBrandLinkedin size={18} className="text-gray-400" />
                        </div>
                        <input
                          type="url"
                          name="linkedin"
                          value={formData.linkedin}
                          onChange={handleChange}
                          placeholder="https://linkedin.com/in/yourprofile"
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                        />
                      </div>
                    </div>

                    {/* Phone Number Input */}
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Phone Number
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <IconPhone size={18} className="text-gray-400" />
                        </div>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Enter your phone number"
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                        />
                      </div>
                    </div>

                    {/* CV Upload Input */}
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Upload CV
                      </label>
                      <div className="relative">
                        <div className="flex items-center justify-center w-full">
                          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <IconUpload size={32} className="text-gray-400 mb-2" />
                              <p className="text-sm text-gray-500 text-center">
                                <span className="font-semibold">Click to upload</span> or drag and drop
                              </p>
                              <p className="text-xs text-gray-400">PDF, DOC, DOCX (MAX. 10MB)</p>
                              {formData.cv && (
                                <p className="text-xs text-blue-600 mt-2 font-medium">
                                  Selected: {formData.cv.name}
                                </p>
                              )}
                            </div>
                            <input
                              type="file"
                              name="cv"
                              onChange={handleChange}
                              className="hidden"
                              accept=".pdf,.doc,.docx"
                            />
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Terms & Conditions */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <div className="relative">
                          <input
                            type="checkbox"
                            id="agreeCheckbox"
                            checked={isChecked}
                            onChange={handleCheckboxChange}
                            className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          />
                          {isChecked && (
                            <IconCheck size={14} className="absolute top-0.5 left-0.5 text-white pointer-events-none" />
                          )}
                        </div>
                        <span className="text-sm text-gray-700 leading-relaxed">
                          I agree to the{" "}
                          <a
                            href="https://app.termly.io/policy-viewer/policy.html?policyUUID=01c438e7-38aa-4cd2-b502-074c52d0d640"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline font-medium"
                          >
                            Terms & Conditions
                          </a>{" "}
                          and{" "}
                          <a
                            href="https://app.termly.io/policy-viewer/policy.html?policyUUID=4283ca3d-0ece-436e-a607-3cad4a5df659"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline font-medium"
                          >
                            Privacy Policy
                          </a>
                        </span>
                      </label>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4">
                      <button
                        type="submit"
                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 rounded-lg transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg flex items-center justify-center gap-2"
                      >
                        Submit Application
                        <IconArrowRight size={18} />
                      </button>
                    </div>
                  </>
                )}
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default JoinWaitlistForm;